<template>
  <div class="container relative flex flex-col w-full h-full overflow-hidden">
    <!-- HEADER -->
    <tx-form-header class="flex flex-col mx-2 my-[30px] grow-0 shrink-0 justify-center header" :title="headerTitle" :show-header="showHeader" />

    <!-- BODY -->
    <div class="flex-auto px-10 mt-4 alerts">
      <tx-alert show :type="type === 'lock' ? 'warning' : 'info'" :text="confirmationMessage" />
      <tx-alert :show="hasError" type="error" :text="errorMessage" dismissible />
    </div>

    <!-- FOOTER -->
    <tx-form-footer
      class="flex flex-row justify-end flex-shrink-0 flex-nowrap"
      :primary-text="confirmButtonText" :primary-disabled="loading" :secondary-disabled="loading"
      @primary-click="onUpdate" @secondary-click="onCancel"
    />
  </div>
</template>

<script setup lang='ts'>
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, watch } from 'vue'
import useErrorMessage from '@/shared/composables/errorMessage'
import { useUserStore } from '@/store/userData'
import TxAlert from '@/shared/components/TxAlert.vue'
import TxFormHeader from '@/shared/components/forms/TxFormHeader.vue'
import TxFormFooter from '@/shared/components/forms/TxFormFooter.vue'
import { updateArticlesLock } from '@/api/t1/article'
import type { UpdateArticlesLockModel } from '@/api/t1/model/articleModel'
import type Article from '@/models/article'
import type MyArticle from '@/models/myArticle'
import appConfig from '@/services/appConfig'

interface IProps {
  type?: 'lock' | 'unlock'
  showHeader?: boolean
  articles: MyArticle[]
  isModel?: boolean
}
const props = withDefaults(defineProps<IProps>(), { type: 'lock', showHeader: true, isModel: false })

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'updated', articles: Article[]): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const { errorMessage, hasError } = useErrorMessage()

const loading = ref(false)

const headerTitle = computed(() => props.type === 'lock' ? t('lockOrUnlockArticle.lockPrices') : t('lockOrUnlockArticle.unlockPrices'))

const confirmationMessage = computed(() => props.type === 'lock'
  ? t('lockOrUnlockArticle.lockArticleConfirmation', { articles: props.isModel ? props.articles.map(a => a.ModelNumber).join(', ') : props.articles.map(a => a.ArticleNumber).join(', ') })
  : t('lockOrUnlockArticle.unlockArticleConfirmation', { articles: props.isModel ? props.articles.map(a => a.ModelNumber).join(', ') : props.articles.map(a => a.ArticleNumber).join(', ') }),
)

const confirmButtonText = computed(() => props.type === 'lock' ? t('lockOrUnlockArticle.lockPrices') : t('lockOrUnlockArticle.unlockPrices'))

function onCancel() {
  emit('cancel')
}

async function onUpdate() {
  if (userStore.activeCatalog && props.articles.length > 0) {
    loading.value = true
    const lock = props.type === 'lock' ? 1 : 0

    const updatedArticles: Article[] = []
    const requestObj: UpdateArticlesLockModel[] = []
    // when browse by model if a model is locked then deactivate all the active article of that model
    if (props.isModel && appConfig.DB && userStore.myAttributes && !userStore.activeCatalog!.IsPriceByStyleApply) {
      const modelIds = [...new Set(props.articles?.map(obj => obj.ModelId))]
      const articles = await appConfig.DB.getArticlesByCatalogCodeModelIds(userStore.activeCatalog, userStore.linkedCatalogDetails, modelIds, userStore.currentCustomer)
      if (articles?.length) {
        articles.forEach((article) => {
          requestObj.push({
            Id: article.Id,
            Lock: lock,
          })
          updatedArticles.push(article)
        })
      }
    }
    else {
      props.articles.forEach((article) => {
        requestObj.push({
          Id: article.Id,
          Lock: lock,
        })
        updatedArticles.push(article)
      })
    }
    updateArticlesLock(userStore.activeCatalog.CatalogCode, props.type === 'lock', requestObj)
      .then(async () => {
        emit('updated', updatedArticles)
      })
      .catch((e) => {
        console.error(e)
        errorMessage.value = t('general.unexpectedError')
      })
      .finally(() => loading.value = false)
  }
}

function reset() {
  loading.value = false
  errorMessage.value = ''
}

watch(() => props, reset)

onMounted(() => {
  reset()
})
</script>
